import React, { Component, ReactNode } from 'react';
import i18n from '../../i18n';
import {
  ErrorContainer,
  ErrorCard,
  ErrorIcon,
  ErrorTitle,
  ErrorMessage,
  ErrorDetails,
  ErrorDetailsTitle,
  ErrorCode,
  ButtonGroup,
  ReloadButton,
  HomeButton,
  ErrorFooter,
} from './styles/errorBoundary.styles';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  autoRecoveryAttempts: number;
}

// Errors most likely caused by EXTERNAL DOM mutation (browser auto-translate,
// extensions, ad-blockers) rather than a real bug in our code. We retry these
// once silently — if it works, the user never sees the error screen.
const AUTO_RECOVERABLE_PATTERNS = [
  'removeChild',
  'insertBefore',
  'NotFoundError',
  'The node to be removed is not a child',
];
const MAX_AUTO_RECOVERY = 1;

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private recoveryTimer: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      autoRecoveryAttempts: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // If the error looks like an external-DOM-mutation glitch and we
    // haven't already tried recovering once this mount, schedule a silent
    // re-render. This rescues the user from "Ups!" pages caused by Chrome
    // auto-translate or browser extensions.
    const msg = `${error?.name ?? ''} ${error?.message ?? ''}`;
    const looksRecoverable = AUTO_RECOVERABLE_PATTERNS.some((p) => msg.includes(p));
    if (looksRecoverable && this.state.autoRecoveryAttempts < MAX_AUTO_RECOVERY) {
      this.setState((s) => ({
        error,
        errorInfo,
        autoRecoveryAttempts: s.autoRecoveryAttempts + 1,
      }));
      this.recoveryTimer = window.setTimeout(() => {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 350);
      return;
    }

    this.setState({ error, errorInfo });
  }

  componentWillUnmount(): void {
    if (this.recoveryTimer !== null) {
      window.clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorCard>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorTitle>{i18n.t('error.oops')}</ErrorTitle>
            <ErrorMessage>
              {i18n.t('error.unexpectedError')}
            </ErrorMessage>

            {this.state.error && (
              <ErrorDetails>
                <ErrorDetailsTitle>{i18n.t('error.errorDetails')}</ErrorDetailsTitle>
                <ErrorCode>{this.state.error.toString()}</ErrorCode>
                {this.state.error.stack && (
                  <ErrorCode style={{ marginTop: 8, whiteSpace: 'pre-wrap', fontSize: 11, opacity: 0.8 }}>
                    {this.state.error.stack.split('\n').slice(0, 12).join('\n')}
                  </ErrorCode>
                )}
                {this.state.errorInfo?.componentStack && (
                  <ErrorCode style={{ marginTop: 8, whiteSpace: 'pre-wrap', fontSize: 11, opacity: 0.8 }}>
                    Component stack:{this.state.errorInfo.componentStack.split('\n').slice(0, 10).join('\n')}
                  </ErrorCode>
                )}
              </ErrorDetails>
            )}

            <ButtonGroup>
              <ReloadButton onClick={this.handleReload}>
                🔄 {i18n.t('error.reloadPage')}
              </ReloadButton>
              <HomeButton onClick={() => window.location.href = '/'}>
                🏠 {i18n.t('error.goHome')}
              </HomeButton>
            </ButtonGroup>

            <ErrorFooter>
              {i18n.t('error.persistsContact')}
            </ErrorFooter>
          </ErrorCard>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
